/**
 * Unsent chat messages that survive a reload (offline plan, docs/offline.md §3.2 / §3.7 / §7.3): the first piece of the
 * durable outbox, for `chat.send` only. Platform-neutral: storage comes from a small KeyValueStore adapter
 * (web: IndexedDB, apps/web/src/lib/idbStore.ts; native later: SQLite / MMKV), one store per user (`hub-<userId>`).
 * Records are kept until the server has the message (same client_message_id, so a resend never duplicates), the person
 * discards it, they are older than 7 days, or the person signs out (wipe).
 */

/** docs/offline.md §7.3: the storage adapter each platform provides. */
export interface KeyValueStore<T> {
  getAll(): Promise<T[]>;
  put(key: string, value: T): Promise<void>;
  delete(key: string): Promise<void>;
  clear(): Promise<void>;
}

export function memoryKeyValueStore<T>(): KeyValueStore<T> {
  const m = new Map<string, T>();
  return {
    getAll: async () => [...m.values()],
    put: async (k, v) => { m.set(k, v); },
    delete: async k => { m.delete(k); },
    clear: async () => { m.clear(); },
  };
}

/** What a pending chat message needs to be shown again and resent (the `chat.send` op payload). */
export interface UnsentChatPayload {
  client_message_id: string;
  body: string;
  parent_id: string | null;
  mentions: string[];
  attachment_ids: string[];
  attachments: unknown[];
  call_id?: string | null;
  created_at: string;
}

/** A stored op, shaped like docs/offline.md's OutboxOp (the subset chat needs). */
export interface UnsentChatOp {
  id: string; // = client_message_id
  kind: 'chat.send';
  userId: string;
  conversationId: string;
  payload: UnsentChatPayload;
  createdAt: string;
  attempts: number;
  state: 'queued' | 'failed';
  /** False when the server refused it (4xx): it waits for Retry instead of resending by itself. */
  retryable: boolean;
  error?: string;
}

export const UNSENT_TTL_MS = 7 * 24 * 3600_000;

/** The fields every durable outbox op has (docs/offline.md OutboxOp); the store needs only these. */
export interface OutboxRecord { id: string; kind: string; userId: string; createdAt: string }

export interface OutboxStore<T extends OutboxRecord> {
  /** The user's ops of this store's kinds, oldest first (expired ones are dropped). Ops of other kinds are left alone. */
  load(userId: string, now?: number): Promise<T[]>;
  save(op: T): Promise<void>;
  remove(userId: string, id: string): Promise<void>;
  /** Sign-out wipe: every op in the user's store, whatever its kind. */
  clear(userId: string): Promise<void>;
}

/**
 * The durable outbox wrapper: one per-user store shared by several op kinds (chat.send, run.*), each consumer seeing
 * only its own kinds. Every call is best-effort (storage can be full, blocked or evicted).
 */
export function createOutboxStore<T extends OutboxRecord>(open: (userId: string) => KeyValueStore<T>, opts: { kinds: readonly string[]; ttlMs?: number }): OutboxStore<T> {
  const ttl = opts.ttlMs ?? UNSENT_TTL_MS;
  const kinds = new Set(opts.kinds);
  const stores = new Map<string, KeyValueStore<T>>();
  const storeOf = (userId: string) => { let s = stores.get(userId); if (!s) { s = open(userId); stores.set(userId, s); } return s; };
  const quiet = async <R>(f: () => Promise<R>, fallback: R): Promise<R> => { try { return await f(); } catch { return fallback; } };
  return {
    async load(userId, now = Date.now()) {
      const s = storeOf(userId);
      const all = await quiet(() => s.getAll(), [] as T[]);
      const keep: T[] = [];
      for (const op of all) {
        if (!kinds.has(op.kind)) continue; // another consumer's op (same IndexedDB table): not ours to judge
        if (op.userId !== userId || now - Date.parse(op.createdAt) > ttl) await quiet(() => s.delete(op.id), undefined);
        else keep.push(op);
      }
      return keep.sort((a, b) => a.createdAt.localeCompare(b.createdAt));
    },
    save: op => quiet(() => storeOf(op.userId).put(op.id, op), undefined),
    remove: (userId, id) => quiet(() => storeOf(userId).delete(id), undefined),
    clear: userId => quiet(() => storeOf(userId).clear(), undefined),
  };
}

export type UnsentMessages = OutboxStore<UnsentChatOp>;

/** Unsent chat messages: the outbox store for `chat.send` ops. */
export function createUnsentMessages(open: (userId: string) => KeyValueStore<UnsentChatOp>, opts: { ttlMs?: number } = {}): UnsentMessages {
  return createOutboxStore<UnsentChatOp>(open, { kinds: ['chat.send'], ttlMs: opts.ttlMs });
}
